/**
 * Same-origin API client. 401 anywhere = bounce through the BFF login.
 *
 * That bounce is a full-page navigation, so the server side of this contract is
 * load-bearing: **401 must mean "no session", never "wrong role"**. A role
 * refusal that arrives as a 401 sends the page to a sign-in that succeeds and
 * returns it to the same refusal, forever. `requireSession` answers a role
 * refusal with 400 for exactly this reason; see `apps/api/src/auth/middleware.ts`.
 */
export class ApiError extends Error {
  constructor(public status: number, public body: unknown) { super(`api ${status}`); }
}

async function sha256Hex(s: string): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function api<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const { json, ...rest } = init ?? {};
  const payload = json !== undefined ? JSON.stringify(json) : (rest.body as string | undefined);
  const method = (rest.method ?? (json !== undefined ? "POST" : "GET")).toUpperCase();
  const headers = new Headers(rest.headers);
  if (json !== undefined) headers.set("content-type", "application/json");
  // CloudFront OAC (SigV4) requires the payload hash on non-GET requests.
  if (method !== "GET" && method !== "HEAD") headers.set("x-amz-content-sha256", await sha256Hex(payload ?? ""));
  // `body` is omitted rather than set to undefined: RequestInit types it as BodyInit | null,
  // and a GET carries no body at all.
  const r = await fetch(path, {
    ...rest, method, headers, credentials: "same-origin",
    ...(payload === undefined ? {} : { body: payload }),
  });
  if (r.status === 401) {
    const dest = encodeURIComponent(location.pathname + location.search + location.hash);
    location.assign(`/api/auth/login?dest=${dest}`);
    throw new ApiError(401, null);
  }
  const body = r.headers.get("content-type")?.includes("json") ? await r.json() : await r.text();
  if (!r.ok) throw new ApiError(r.status, body);
  return body as T;
}
